<template>
  <div class="comment-section" :class="{ 'comment-section--embedded': embedded }">
    <div class="comment-section__header d-flex align-items-center mb-3">
      <span class="font-weight-bold font-size-sm">
        Bình luận
        <span v-if="total > 0" class="text-muted">({{ total }})</span>
      </span>
      <b-spinner v-if="loading" small class="ml-2" />
    </div>

    <!-- Load more older comments -->
    <div v-if="hasMore" class="comment-section__loadmore mb-2">
      <button
        class="comment-section__loadmore-btn"
        :disabled="loadingMore"
        @click="loadMore"
      >
        <b-spinner v-if="loadingMore" small class="mr-1" />
        <i v-else class="fas fa-chevron-up mr-1" />
        Xem thêm {{ remainingCount }} bình luận trước đó
      </button>
    </div>

    <div class="comment-section__body">
    <!-- Empty state -->
    <div v-if="!loading && total === 0" class="comment-section__empty text-center py-4">
      <i class="fas fa-comments text-muted mb-2 comment-section__empty-icon" />
      <p class="text-muted font-size-sm mb-0">Chưa có bình luận nào</p>
    </div>

    <!-- Comment list -->
    <div v-else class="comment-section__list mb-3">
      <CommentItem
        v-for="comment in comments"
        :key="comment.id"
        :comment="comment"
        :current-user-id="currentUserId"
        :module="module"
        :reference-id="referenceId"
        :unread-ids="unreadIds"
        @deleted="onRootDeleted"
        @edited="onRootEdited"
        @reply-added="onReplyAdded"
        @reply-deleted="onReplyDeleted"
        @reply-edited="onReplyEdited"
      />
    </div>
    </div>

    <!-- Root comment input -->
    <CommentInput
      :module="module"
      :reference-id="referenceId"
      :parent-id="null"
      @submitted="onCommentSubmitted"
      @error="onSubmitError"
    />
  </div>
</template>

<script>
import ApiService from '@/core/services/api.service';
import { makeToastFaile } from '@/utils/common';
import CommentItem from './CommentItem.vue';
import CommentInput from './CommentInput.vue';
import { getUserInfoFromStorage } from '@/utils/saveDataToLocal';

export default {
  name: 'CommentSection',

  components: { CommentItem, CommentInput },

  props: {
    module: { type: String, required: true },
    referenceId: { type: [String, Number], required: true },
    embedded: { type: Boolean, default: false },
  },

  data() {
    return {
      comments: [],
      loading: false,
      loadingMore: false,
      total: 0,
      offset: 0,
      limit: 10,
    };
  },

  computed: {
    currentUserId() {
      return getUserInfoFromStorage()?.userId || null;
    },
    hasMore() {
      return this.offset < this.total;
    },
    remainingCount() {
      return Math.max(this.total - this.offset, 0);
    },
  },

  watch: {
    referenceId(val) {
      if (val) this.resetAndFetch();
    },
  },

  created() {
    if (this.referenceId) this.resetAndFetch();
  },

  methods: {
    resetAndFetch() {
      this.comments = [];
      this.total = 0;
      this.offset = 0;
      this.fetchComments();
    },

    async fetchComments() {
      this.loading = true;
      try {
        const { data } = await ApiService.query('comments', {
          params: {
            module: this.module,
            referenceId: this.referenceId,
            limit: this.limit,
            offset: 0,
          },
        });
        const result = data.data || {};
        this.comments = result.data || [];
        this.total = result.total || 0;
        this.offset = this.comments.length;
        this.markMentionsRead();
      } catch {
        makeToastFaile('Tải bình luận thất bại');
      } finally {
        this.loading = false;
      }
    },

    async loadMore() {
      if (this.loadingMore || !this.hasMore) return;
      this.loadingMore = true;
      try {
        const { data } = await ApiService.query('comments', {
          params: {
            module: this.module,
            referenceId: this.referenceId,
            limit: this.limit,
            offset: this.offset,
          },
        });
        const result = data.data || {};
        const older = result.data || [];
        this.comments = [...older, ...this.comments];
        this.total = result.total || 0;
        this.offset += older.length;
      } catch {
        makeToastFaile('Tải bình luận thất bại');
      } finally {
        this.loadingMore = false;
      }
    },

    markMentionsRead() {
      ApiService.post('comments/mark-mentions-read', {
        module: this.module,
        referenceId: String(this.referenceId),
      }).catch(() => {});
    },

    onCommentSubmitted(newComment) {
      this.comments.push({ ...newComment, replies: [] });
      this.total++;
      this.offset++;
    },

    onRootDeleted(commentId) {
      const idx = this.comments.findIndex((c) => c.id === commentId);
      if (idx === -1) return;

      const hasReplies = this.comments[idx].replies && this.comments[idx].replies.length > 0;
      if (hasReplies) {
        this.$set(this.comments, idx, {
          ...this.comments[idx],
          deleted: true,
          content: 'Bình luận đã bị xóa',
          author: null,
          attachments: [],
          isEdited: false,
        });
      } else {
        this.comments.splice(idx, 1);
        this.total = Math.max(this.total - 1, 0);
        this.offset = Math.max(this.offset - 1, 0);
      }
    },

    onRootEdited(updatedComment) {
      const idx = this.comments.findIndex((c) => c.id === updatedComment.id);
      if (idx !== -1) {
        this.$set(this.comments, idx, { ...this.comments[idx], ...updatedComment });
      }
    },

    onReplyAdded({ parentId, reply }) {
      const parent = this.comments.find((c) => c.id === parentId);
      if (!parent) return;
      if (!parent.replies) this.$set(parent, 'replies', []);
      parent.replies.push(reply);
    },

    onReplyDeleted(replyId) {
      this.comments.forEach((c) => {
        if (!c.replies) return;
        const idx = c.replies.findIndex((r) => r.id === replyId);
        if (idx !== -1) c.replies.splice(idx, 1);
      });
    },

    onReplyEdited(updatedReply) {
      this.comments.forEach((c) => {
        if (!c.replies) return;
        const idx = c.replies.findIndex((r) => r.id === updatedReply.id);
        if (idx !== -1) this.$set(c.replies, idx, { ...c.replies[idx], ...updatedReply });
      });
    },

    onSubmitError() {
      makeToastFaile('Gửi bình luận thất bại');
    },
  },
};
</script>

<style src="./styles/comment-section.css" scoped></style>
